import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../services/supabaseClient';
import { createBook, listTemplates, getLatestBookWithPhoto } from '../../services/compositionApi';
import { listPrintFormats } from '../../services/ordersApi';
import { ensureSession } from '../../services/anonymousSession';
import { formatPriceCents } from '../../utils/orderWorkflow';
import Icon from '../common/Icon';
import FormatBook from './FormatBook';
import FormatInteriorModal from './FormatInteriorModal';
import './CreateBookSansIA.css';

// Taille en cm avec au plus une decimale (21 x 29,7 ; 21,6 x 27,9) — jamais arrondie a l'entier, qui ferait
// passer un A4 pour un 21 x 30 et un Letter pour un 22 x 28.
const formatCmFor = (mm, lang) => (
  new Intl.NumberFormat(lang === 'en' ? 'en-GB' : 'fr-FR', { maximumFractionDigits: 1 }).format(Number(mm) / 10)
);

// Barre d'etapes (maquette "Maquettes Bookipix", 2026-10-11) : situe les 2
// ecrans de cet assistant dans le parcours complet de creation — l'Atelier
// et la Commande suivent, sur d'autres routes, jamais "termines" depuis ici.
const STEP_NAMES_KEY = ['typeStep.stepperType', 'typeStep.stepperFormat', 'typeStep.stepperAtelier', 'typeStep.stepperOrder'];
function WizardStepper({ t, current }) {
  return (
    <ol className="wiz-stepper" aria-label={t('typeStep.stepperLabel')}>
      {STEP_NAMES_KEY.map((key, index) => (
        <li key={key} className={index < current ? 'is-done' : index === current ? 'is-on' : ''}>
          <span className="wiz-stepper-n">{index < current ? <Icon name="check" size={14} /> : index + 1}</span>
          <span>{t(key)}</span>
        </li>
      ))}
    </ol>
  );
}

const RETURN_TO_KEY = 'returnTo';
const DRAFT_KEY = 'createBookDraftSansIA';

// Parcours d'entree, en 2 ou 3 ecrans sur la meme route : type de livre ->
// (collaboratif seulement) pour qui -> FORMAT. Jamais tout sur un seul ecran.
//
// DEUX DECISIONS PRODUIT DU 2026-09-15 :
//
// 1. PLUS DE TITRE ICI. Il etait demande, et obligatoire. C'est l'etape la
//    plus couteuse du parcours pour la moins utile : au moment de creer, on
//    ne sait souvent pas encore comment on va appeler son livre. Le titre se
//    saisit desormais dans l'atelier, au moment de la couverture — la ou on
//    le voit, ou il prend son sens. Le livre se cree sans titre (la colonne
//    l'accepte, et la couverture affiche un libelle de repli).
//
// 2. LE FORMAT SE CHOISIT AVANT L'ATELIER. Il n'est pas cosmetique : il
//    decide de la densite de composition, de la taille reelle des cadres
//    photo — donc de la resolution necessaire — et du prix. Le choisir apres
//    coup revenait a recomposer un livre deja fait. Prix affiche des cet
//    ecran : on doit savoir ce qu'on fabrique.
export default function CreateBookSansIA() {
  const { t, i18n } = useTranslation('createBook');
  const formatCm = (mm) => formatCmFor(mm, i18n.language);
  const priceLocale = i18n.language === 'en' ? 'en-US' : 'fr-FR';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState('type'); // 'type' | 'format'
  const [mode, setMode] = useState(null); // 'solo' | 'open'
  const [printFormat, setPrintFormat] = useState(null);
  // Format dont on regarde l'interieur (fenetre « Voir l'interieur »), ou null.
  const [interiorFormatId, setInteriorFormatId] = useState(null);
  const [formats, setFormats] = useState([]);
  // Couverture MONTREE sur les trois cartes (elles changent ensemble) : 'full' | 'frame' | 'type'.
  const [coverStyle, setCoverStyle] = useState('full');
  // Derniere couverture du visiteur CONNECTE (livre avec au moins une photo) ; null = couverture d'exemple.
  const [ownBookId, setOwnBookId] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const user = session && session.user;
        if (!user || user.is_anonymous) return;
        const { bookId } = await getLatestBookWithPhoto();
        if (bookId && !cancelled) setOwnBookId(bookId);
      } catch (_error) { /* non bloquant : couverture d'exemple */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const [currency, setCurrency] = useState('EUR');
  const [recipientName, setRecipientName] = useState('');
  const [eventDate, setEventDate] = useState('');
  // Occasion : jamais redemandee explicitement (cahier des charges §9) —
  // seulement portee silencieusement si on arrive depuis la grille
  // d'occasions de l'accueil (`?event=mariage`), sinon absente.
  const [eventType] = useState(searchParams.get('event') || '');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  // Premier template actif du catalogue, pose par defaut a la creation : sans
  // template_id, "Passer en mode automatique" (POST /compose, voir
  // routes/composition.js) refuse de composer (422) — un livre doit pouvoir
  // generer automatiquement des sa creation. Modifiable ensuite (Configuration,
  // section Style) — jamais impose de facon definitive. Promesse conservee
  // (pas juste un state) pour que handleCreate() puisse toujours l'attendre,
  // meme si le clic arrive avant que la requete initiale n'ait fini (ex. le
  // chemin d'auto-soumission au retour d'authentification, ci-dessous).
  const defaultTemplatePromiseRef = useRef(null);
  if (!defaultTemplatePromiseRef.current) {
    defaultTemplatePromiseRef.current = listTemplates()
      .then((templates) => templates?.[0]?.id || null)
      .catch(() => null);
  }

  // Catalogue des formats : dimensions reelles et prix de depart, calcules
  // par le serveur avec la MEME formule que le prix facture (voir
  // routes/orders.js GET /formats). Jamais une table recopiee ici, qui
  // finirait par annoncer un prix different de celui paye.
  useEffect(() => {
    let annule = false;
    const charger = () => {
      listPrintFormats()
        .then((data) => {
          if (annule) return;
          setFormats(data?.formats || []);
          setCurrency(data?.currency || 'EUR');
          setPrintFormat((actuel) => actuel || data?.defaultFormatId || null);
        })
        .catch(() => { /* non bloquant : l'ecran de format affiche alors un repli */ });
    };
    charger();
    // Pays choisi dans l'en-tete (CountrySwitcher.js, chantier
    // international 2026-10-02) : redemande les tarifs dans la nouvelle
    // devise si le visiteur change de pays APRES ce premier chargement.
    const onCountryChange = () => charger();
    window.addEventListener('bookipix:country-changed', onCountryChange);
    return () => {
      annule = true;
      window.removeEventListener('bookipix:country-changed', onCountryChange);
    };
  }, []);

  const buildPayload = async (values) => ({
    // Le titre n'est plus demande ici (2026-09-15) : il se saisit dans
    // l'atelier, au moment de la couverture. On envoie une CHAINE VIDE et non
    // null — la colonne books.title est NOT NULL en base, et une migration
    // pour un champ qu'on ne demande justement plus serait disproportionnee.
    // Tous les affichages ont un repli ("Mon livre", "Livre souvenir").
    title: '',
    print_format: values.printFormat,
    event_type: eventType || null,
    collection_mode: values.mode,
    template_id: await defaultTemplatePromiseRef.current,
    // Palier minimum deja etabli ailleurs (generation automatique, voir
    // BookAtelierLuxe.js: MIN_AUTO_PAGES), pose ici pour que l'atelier soit
    // utilisable immediatement (il refuse de s'ouvrir sans page_count) —
    // jamais impose au client : page_count_mode absent = 'auto' par defaut
    // (BookConfigLuxe.js), qui recalculera ce nombre depuis le contenu reel
    // des que l'utilisateur passe par Configuration. 2026-09-09 : 28 (pas
    // 16) — minimum reellement imprimable chez Gelato ; 2026-09-11 : porte a
    // 30, voir backend/services/composition/layoutEngine.js PAGE_COUNT_TIERS.
    // Minimum du FORMAT choisi : 30 pour les formats historiques, celui du fournisseur sinon (18 pour un
    // layflat Prodigi) — le nombre de pages d'un livre neuf est le plus petit nombre que le fournisseur accepte.
    page_count: (formats.find((entry) => entry.formatId === values.printFormat) || {}).minPages || 30,
    // Renseignes seulement s ils ont ete repris d un brouillon : le
    // parcours ne les demande plus (voir chooseMode). Ils restent
    // modifiables dans Configuration et sur la couverture.
    ...(values.mode === 'open' && values.recipientName?.trim()
      ? { recipient_name: values.recipientName.trim(), event_date: values.eventDate || null }
      : {})
  });

  async function createAndRedirect(values) {
    setCreating(true);
    setError('');
    try {
      // Droit dans l'atelier : c'est la suite naturelle du parcours
      // (JE CHOISIS -> JE CREE). Passer par le tableau de bord ajoutait un
      // ecran ou l'utilisateur devait re-trouver le livre qu'il venait de
      // creer.
      const livre = await createBook(await buildPayload(values));
      localStorage.removeItem(DRAFT_KEY);
      navigate(livre?.id ? `/book/${livre.id}/atelier` : '/dashboard');
    } catch (err) {
      setError(err.message || t('errorCreation'));
      setCreating(false);
    }
  }

  // Reprend un brouillon laisse avant un passage par la creation de compte —
  // et, nouveau, le soumet automatiquement si une session existe maintenant
  // (retour d'authentification) : c'est ce qui realise "creer automatiquement
  // le livre apres connexion", sans dupliquer la logique de creation.
  useEffect(() => {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return;
    let draft;
    try {
      draft = JSON.parse(raw);
    } catch (_err) {
      localStorage.removeItem(DRAFT_KEY);
      return;
    }

    if (draft.mode) setMode(draft.mode);
    if (draft.printFormat) setPrintFormat(draft.printFormat);
    if (draft.recipientName) setRecipientName(draft.recipientName);
    if (draft.eventDate) setEventDate(draft.eventDate);
    // Un brouillon laisse AVANT le retrait de l etape « pour qui »
    // (2026-09-22) peut encore designer 'details', qui n existe plus :
    // on repart du format, la seule question restante.
    if (draft.mode) setStep('format');

    // Le format remplace le titre comme condition de reprise : c'est
    // desormais le dernier choix du parcours, donc le signe qu'il etait
    // complet au moment ou l'authentification l'a interrompu.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session && draft.printFormat && draft.mode) {
        createAndRedirect(draft);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function chooseMode(nextMode) {
    setMode(nextMode);
    // PLUS AUCUNE QUESTION AVANT LE FORMAT (2026-09-22).
    //
    // Le mode collaboratif exigeait encore un prenom — « pour qui est ce
    // livre ? » — au motif que les proches recevant le lien devaient le
    // savoir. L'intention etait juste, le blocage ne l'etait pas : on
    // arretait quelqu'un venu creer un livre pour lui demander un mot qu'il
    // pourra donner plus tard, et qui n'est utilise nulle part avant
    // l'invitation.
    //
    // Le prenom du destinataire et la date restent modifiables dans
    // Configuration et sur la couverture, et le message d'invitation les
    // reprend quand ils existent. Le solo avait deja perdu sa derniere
    // question de la meme facon (2026-09-15) ; les deux parcours se
    // rejoignent.
    setStep('format');
  }

  async function handleCreate() {
    if (!printFormat) {
      setError(t('errorChooseFormat'));
      return;
    }
    setError('');

    const values = { mode, printFormat, recipientName, eventDate };

    // Demarrage SANS COMPTE (2026-09-12) : plus aucun mur d'inscription ici.
    // Sans session, on en ouvre une anonyme et on cree le livre tout de
    // suite — le compte sera demande au moment de commander, quand la valeur
    // a deja ete vue. C'est ce qui permet de deposer ses photos et de
    // composer avant de s'engager.
    const { unavailable } = await ensureSession();
    if (unavailable) {
      // Repli sur l'ancien parcours : si la connexion anonyme est
      // indisponible (option desactivee cote Supabase, reseau), on ne bloque
      // pas la creation — on repasse par l'inscription, rien n'est perdu.
      localStorage.setItem(DRAFT_KEY, JSON.stringify(values));
      localStorage.setItem(RETURN_TO_KEY, '/create-book');
      navigate('/register');
      return;
    }

    createAndRedirect(values);
  }

  return (
    <div className="wizard-container create-book-sans-ia">
      <div className="wiz-wrap">
        <WizardStepper t={t} current={step === 'type' ? 0 : 1} />
        <div className="wizard-card">
          <section className="wizard-content">
          {step === 'type' && (
            <div className="ab-step">
              <span className="event-badge">{t('newBookBadge')}</span>
              <h2 className="form-title">{t('typeStep.title')}</h2>

              <div className="create-mode-grid">
                <button type="button" className="type-choice-card" onClick={() => chooseMode('solo')}>
                  <span className="type-choice-icon"><Icon name="user" /></span>
                  <span className="ab-select-title">{t('typeStep.soloTitle')}</span>
                  <span className="ab-select-subtitle">{t('typeStep.soloSubtitle')}</span>
                </button>
                <button type="button" className="type-choice-card" onClick={() => chooseMode('open')}>
                  <span className="type-choice-icon"><Icon name="users" /></span>
                  <span className="ab-select-title">{t('typeStep.collectiveTitle')}</span>
                  <span className="ab-select-subtitle">
                    {t('typeStep.collectiveSubtitle')}
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* CHOIX DU FORMAT, avant l'atelier.
              Il n'est pas cosmetique : il decide de la densite de composition,
              de la taille reelle des cadres photo — donc de la resolution
              necessaire — et du prix. Le prix vient du serveur, calcule avec
              la meme formule que celui facture a la commande. */}
          {step === 'format' && (
            <div className="ab-step">
              <button
                type="button"
                className="ab-back-link"
                onClick={() => setStep('type')}
              >
                {t('formatStep.back')}
              </button>
              <h2 className="form-title">{t('formatStep.title')}</h2>
              <p className="ab-step-hint">
                {t('formatStep.hint')}
              </p>

              {error ? <div className="wizard-error">{error}</div> : null}

              <div className="format-cover-picker">
                <label>{t('formatStep.coverShown')}</label>
                <div className="format-cover-seg" role="group" aria-label={t('formatStep.coverStyleLabel')}>
                  {['full', 'frame', 'type'].map((key) => (
                    <button key={key} type="button" aria-pressed={coverStyle === key} onClick={() => setCoverStyle(key)}>
                      {t(`formatStep.coverStyles.${key}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="format-choice-grid">
                {formats.map((format) => (
                  <div key={format.formatId} className="format-choice-item">
                  <button
                    type="button"
                    className={`format-choice-card ${printFormat === format.formatId ? 'is-selected' : ''} ${format.recommande ? 'is-recommended' : ''}`}
                    onClick={() => { setPrintFormat(format.formatId); setError(''); }}
                    aria-pressed={printFormat === format.formatId}
                  >
                    {format.recommande && <span className="format-choice-badge">{t('formatStep.recommended')}</span>}
                    {format.previewOnly && <span className="format-choice-badge format-choice-badge-preview">{t('formatStep.testersPreview')}</span>}
                    <div className="format-shelf">
                      <FormatBook
                        formatId={format.formatId}
                        widthMm={format.widthMm}
                        heightMm={format.heightMm}
                        reliure={format.reliure}
                        style={coverStyle}
                        lang={i18n.language}
                        ownBookId={ownBookId}
                        alt={t('formatStep.bookAlt', {
                          name: format.nom,
                          width: formatCm(format.widthMm),
                          height: formatCm(format.heightMm),
                          cover: format.reliure === 'rigide' ? t('formatStep.hardcoverFull') : t('formatStep.softcoverFull')
                        })}
                      />
                    </div>
                    {/* format.nom/accroche viennent du catalogue serveur
                        (GET /orders/formats) : nom jamais traduit (nom de
                        produit), accroche bilingue depuis le chantier phase 6
                        (resolue cote serveur via le meme en-tete X-App-Language
                        que tous les autres appels, voir httpClient.js). */}
                    <span className="format-choice-name">{format.nom}</span>
                    <span className="format-choice-size">
                      {formatCm(format.widthMm)} × {formatCm(format.heightMm)} cm · {t('formatStep.coverLabel')} {format.reliure === 'rigide' ? t('formatStep.hardcover') : t('formatStep.softcover')}
                    </span>
                    <span className="format-choice-pitch">{format.accroche}</span>
                    <span className="format-choice-compare">{t(`formatStep.compare.${format.formatId}`, { defaultValue: '' })}</span>
                    <span className="format-choice-specs">
                      <span className={`format-choice-chip ${format.binding === 'layflat' ? 'is-flat' : ''}`}>
                        {format.binding === 'layflat' ? t('formatStep.bindingLayflat') : t('formatStep.bindingGlued')}
                      </span>
                      {format.paper ? <span className="format-choice-chip">{format.paper}</span> : null}
                    </span>
                    <span className="format-choice-price">
                      {t('formatStep.startingFrom')} {formatPriceCents(format.startingPriceCents, currency, priceLocale)}
                      <small>{t('formatStep.forPages', { count: format.minPages })}</small>
                    </span>
                  </button>
                  <button type="button" className="format-choice-inside" onClick={() => setInteriorFormatId(format.formatId)}>
                    <Icon name="eye" size={16} /> {t('formatStep.seeInside')}
                  </button>
                  </div>
                ))}
              </div>

              {formats.length === 0 && (
                <p className="ab-step-hint">{t('formatStep.loadingFormats')}</p>
              )}

              {interiorFormatId && formats.find((f) => f.formatId === interiorFormatId) && (
                <FormatInteriorModal
                  format={formats.find((f) => f.formatId === interiorFormatId)}
                  coverStyle={coverStyle}
                  lang={i18n.language}
                  ownBookId={ownBookId}
                  priceLabel={(() => {
                    const f = formats.find((x) => x.formatId === interiorFormatId);
                    return formatPriceCents(f.startingPriceCents, currency, priceLocale);
                  })()}
                  selected={printFormat === interiorFormatId}
                  onClose={() => setInteriorFormatId(null)}
                  onChoose={() => { setPrintFormat(interiorFormatId); setError(''); setInteriorFormatId(null); }}
                />
              )}

              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreate}
                disabled={creating || !printFormat}
              >
                {creating ? t('formatStep.submitting') : t('formatStep.submit')}
              </button>
            </div>
          )}

          </section>
        </div>
      </div>
    </div>
  );
}
